// 視聴側 (Phase 4): 1〜3 トラックを受け取り、選択されたレイアウトプリセットで配置。
// レイアウト選好は LocalStorage に保存して次回接続時に自動適用。
//
// 自動再生ポリシー: 接続ボタンというユーザ操作起点で開始するので問題なし。

import type { FreeLayoutItem } from "@wincast/shared";
import {
  PINCODE_LENGTH,
  VIEWER_RELOAD_RECONNECT_DELAY_MS,
} from "@wincast/shared";
import { clamp, parsePct } from "./utils.js";
import {
  clearPincodeFromSession,
  getPincodeFromSession,
  normalizePincode,
} from "./session-store.js";
import {
  applyHtmlI18n,
  getLocale,
  onLocaleChange,
  setLocale,
  t,
  type Locale,
} from "./i18n.js";
import {
  computeLayoutAspect,
  getFreeLayout,
  getLayoutForCount,
  getLayoutIconSvg,
  getLayoutOptions,
  loadSettings,
  makeDefaultFreeLayout,
  persistFreeLayout,
  persistPreferredLayout,
  type LayoutKey,
} from "./layout.js";
import { initStageZoom, resetStageZoom } from "./stage-zoom.js";
import { initJoinBg } from "./join-bg.js";
import { initHeroAnim } from "./hero-anim.js";
import * as annotations from "./annotations.js";
import type { Color as AnnotColor, Tool as AnnotTool } from "./annotations.js";
import {
  initStatusUi,
  rerenderStatusI18n,
  setFormStatus,
  setFormStatusKey,
  setStageStatus,
  setStageStatusKey,
} from "./status-ui.js";
import {
  applyZoomState,
  initViewport,
  isZoomedIn,
  scheduleOverlayHide,
  showJoin,
  sizeGridToViewport,
} from "./viewport.js";
import {
  initMediaElements,
  metadataByStreamId,
  setAudioMuted,
  videoByStreamId,
} from "./media-elements.js";
import {
  cancelReconnect,
  connect,
  initSignalingClient,
  teardown,
} from "./signaling-client.js";

const els = {
  joinView: query<HTMLElement>("#join-view"),
  form: query<HTMLFormElement>("#join-form"),
  input: query<HTMLInputElement>("#pincode"),
  joinButton: query<HTMLButtonElement>("#join-button"),
  status: query<HTMLParagraphElement>("#status"),
  langToggle: query<HTMLButtonElement>("#lang-toggle"),
  stageView: query<HTMLElement>("#stage-view"),
  videoGrid: query<HTMLDivElement>("#video-grid"),
  sessionWarning: query<HTMLDivElement>("#session-warning"),
  remoteAudio: query<HTMLAudioElement>("#remote-audio"),
  stageOverlay: query<HTMLDivElement>("#stage-overlay"),
  stageStatus: query<HTMLSpanElement>("#stage-status"),
  layoutPicker: query<HTMLDivElement>("#layout-picker"),
  layoutPickerToggle: query<HTMLButtonElement>("#layout-picker-toggle"),
  layoutPickerCurrent: query<HTMLSpanElement>("#layout-picker-current"),
  layoutPickerPopup: query<HTMLDivElement>("#layout-picker-popup"),
  audioMuteBtn: query<HTMLButtonElement>("#audio-mute-btn"),
  fullscreenBtn: query<HTMLButtonElement>("#fullscreen-btn"),
  leaveButton: query<HTMLButtonElement>("#leave-button"),
  annotatePicker: query<HTMLDivElement>("#annotate-picker"),
  annotateToggle: query<HTMLButtonElement>("#annotate-toggle"),
  annotateCurrentDot: query<HTMLSpanElement>("#annotate-current-dot"),
  annotatePopup: query<HTMLDivElement>("#annotate-popup"),
  annotateUndo: query<HTMLButtonElement>("#annotate-undo"),
  annotateClear: query<HTMLButtonElement>("#annotate-clear"),
};

initStatusUi({ statusEl: els.status, stageStatusEl: els.stageStatus });
initViewport({
  joinView: els.joinView,
  stageView: els.stageView,
  stageOverlay: els.stageOverlay,
  videoGrid: els.videoGrid,
});
initMediaElements(
  {
    videoGrid: els.videoGrid,
    remoteAudio: els.remoteAudio,
    audioMuteBtn: els.audioMuteBtn,
  },
  { refreshLayout, updateLayoutAspect },
);
initSignalingClient(
  { joinButton: els.joinButton, sessionWarning: els.sessionWarning },
  { refreshLayout, resetLayoutState },
);

// signaling-client の teardown から呼ばれる、レイアウト/スワップ/room の状態リセット。
function resetLayoutState(): void {
  positionOrder = [];
  selectedSwapIndex = null;
  roomLookMoved = false;
}

// 全レイアウト共通のスロット順。data-index の表示順序を保持する。
// - 非対称系 (pip / main-right / main-bottom): positionOrder[0] がメイン
// - 対称系 (horizontal / vertical / columns / rows / room): スロット 0..n-1 の並び
// teardown でリセット。video 追加/削除で ensurePositionOrder() が補正。
let positionOrder: (1 | 2 | 3)[] = [];

// 対称系で「1 個目に選んだ」video index。null = 未選択。
// 2 個目をクリックすると 2 つを入れ替えて null に戻る。
let selectedSwapIndex: 1 | 2 | 3 | null = null;

// 3D 部屋でドラッグ操作 (= 視点回転) が起きた場合に、続く click イベントを
// 無視するためのフラグ。click は pointerup 後に発火するが、ドラッグ後でも
// 発火するブラウザがあるため明示的に抑制する。
let roomLookMoved = false;

let settings = loadSettings();

function query<T extends Element>(selector: string): T {
  const node = document.querySelector<T>(selector);
  if (!node) throw new Error(`viewer: ${selector} not found`);
  return node;
}

function isAsymmetricLayout(): boolean {
  const layout = els.videoGrid.dataset.layout ?? "";
  const count = videoByStreamId.size;
  return (
    (count === 2 && layout === "pip") ||
    (count === 3 && (layout === "main-right" || layout === "main-bottom"))
  );
}

function isSymmetricSwappableLayout(): boolean {
  const layout = els.videoGrid.dataset.layout ?? "";
  const count = videoByStreamId.size;
  return (
    (count === 2 && (layout === "horizontal" || layout === "vertical")) ||
    (count === 3 &&
      (layout === "columns" || layout === "rows" || layout === "room"))
  );
}

function isReorderableLayout(): boolean {
  return isAsymmetricLayout() || isSymmetricSwappableLayout();
}

// 現在の videoByStreamId から positionOrder を補正する。
// - 既に使われない index は削除
// - 新しく増えた index は末尾に追加 (data-index 昇順)
function ensurePositionOrder(): void {
  const available: (1 | 2 | 3)[] = [];
  for (const v of videoByStreamId.values()) {
    const idx = Number(v.dataset.index);
    if (idx === 1 || idx === 2 || idx === 3) {
      available.push(idx as 1 | 2 | 3);
    }
  }
  positionOrder = positionOrder.filter((i) => available.includes(i));
  available.sort((a, b) => a - b);
  for (const i of available) {
    if (!positionOrder.includes(i)) positionOrder.push(i);
  }
}

function reorderVideosByMetaIndex(): void {
  // 並べ替え可能なレイアウトでは positionOrder の順、それ以外は data-index 昇順。
  // free / room はそもそも bare video が grid 直下にないので、ここでのソートは
  // 影響しない (room は renderRoomMode が positionOrder を直接参照する)。
  ensurePositionOrder();
  const useOrder = isReorderableLayout();

  const videos = Array.from(
    els.videoGrid.querySelectorAll<HTMLVideoElement>(":scope > video"),
  );
  videos.sort((a, b) => {
    const ai = Number(a.dataset.index ?? "99") as 1 | 2 | 3;
    const bi = Number(b.dataset.index ?? "99") as 1 | 2 | 3;
    if (useOrder) {
      const oa = positionOrder.indexOf(ai);
      const ob = positionOrder.indexOf(bi);
      return (oa < 0 ? 99 : oa) - (ob < 0 ? 99 : ob);
    }
    return ai - bi;
  });
  for (const v of videos) {
    els.videoGrid.appendChild(v);
  }
}

// ----- スワップ操作 -----

// 非対称系: 指定 idx をメイン (positionOrder[0]) に。
// 旧 mainIndex の挙動と一致させるため「指定 idx を先頭、他は data-index 昇順」。
function makeMain(idx: 1 | 2 | 3): void {
  if (positionOrder[0] === idx) return;
  const others: (1 | 2 | 3)[] = [];
  for (const v of videoByStreamId.values()) {
    const n = Number(v.dataset.index);
    if ((n === 1 || n === 2 || n === 3) && n !== idx) {
      others.push(n as 1 | 2 | 3);
    }
  }
  others.sort((a, b) => a - b);
  positionOrder = [idx, ...others];
  refreshLayout();
}

// 対称系: positionOrder 上で a と b のスロットを入れ替える (他は維持)。
function swapInOrder(a: 1 | 2 | 3, b: 1 | 2 | 3): void {
  const ia = positionOrder.indexOf(a);
  const ib = positionOrder.indexOf(b);
  if (ia < 0 || ib < 0 || ia === ib) return;
  const next = positionOrder.slice();
  [next[ia], next[ib]] = [next[ib]!, next[ia]!];
  positionOrder = next;
  refreshLayout();
}

function handleSwapClick(idx: 1 | 2 | 3): void {
  if (selectedSwapIndex === null) {
    selectedSwapIndex = idx;
    applySwapSelection();
  } else if (selectedSwapIndex === idx) {
    // 同じ video を再クリック → 選択解除
    clearSwapSelection();
  } else {
    const a = selectedSwapIndex;
    clearSwapSelection();
    swapInOrder(a, idx);
  }
}

function applySwapSelection(): void {
  for (const v of videoByStreamId.values()) {
    if (Number(v.dataset.index) === selectedSwapIndex) {
      v.classList.add("swap-selected");
    } else {
      v.classList.remove("swap-selected");
    }
  }
  for (const [i, wall] of roomWallEls) {
    if (i === selectedSwapIndex) wall.classList.add("swap-selected");
    else wall.classList.remove("swap-selected");
  }
}

function clearSwapSelection(): void {
  selectedSwapIndex = null;
  for (const v of videoByStreamId.values()) {
    v.classList.remove("swap-selected");
  }
  for (const wall of roomWallEls.values()) {
    wall.classList.remove("swap-selected");
  }
}

function applyLayout(): void {
  // レイアウトが変わると基準座標が無意味になるのでアプリ内ズームを毎回リセット
  resetStageZoom();
  const count = videoByStreamId.size;
  els.videoGrid.dataset.count = String(count);
  if (count === 0) {
    els.videoGrid.dataset.layout = "";
    els.layoutPicker.hidden = true;
    els.layoutPickerPopup.innerHTML = "";
    closeLayoutPopup();
    els.annotatePicker.hidden = true;
    closeAnnotatePopup();
    annotations.setTool("off");
    refreshAnnotateUI();
    unwrapFreeMode();
    unwrapRoomMode();
    updateLayoutAspect();
    return;
  }
  const opts = getLayoutOptions(count);
  const layout = getLayoutForCount(count, settings) ?? opts[0]?.value;
  if (layout) {
    els.videoGrid.dataset.layout = layout;
  }
  // popup を再構築。アイコンのみで、tooltip (title) に名前を載せる。
  rebuildLayoutPicker(opts, layout ?? null);
  els.layoutPicker.hidden = false;

  // 一旦 free / room の wrap を解除して bare video を grid 直下に戻す。
  // その上で nth-child 用に並び直してから新レイアウトを適用する。
  unwrapFreeMode();
  unwrapRoomMode();
  reorderVideosByMetaIndex();

  if (layout === "free") {
    // preset の inline aspect-ratio が残っているとステージ高さが縮むので
    // updateLayoutAspect で除去 (内部で free/room モード時は early return)
    updateLayoutAspect();
    wrapForFreeMode();
    // 保存済みレイアウトの width/height が現アスペクトと食い違っていれば
    // ここで面積保持リフィットして letterbox を出さない。
    refitAllFreeItemsToAspect();
  } else if (layout === "room") {
    updateLayoutAspect();
    renderRoomMode();
  } else {
    updateLayoutAspect();
  }

  // 3D room レイアウト時はアノテーション overlay を全て非表示にし、
  // ツールバーボタンも隠す (壁の 3D 回転に追従できないため)。
  const annotEnabled = layout !== "room";
  annotations.setEnabled(annotEnabled);
  els.annotatePicker.hidden = !annotEnabled;
  if (!annotEnabled) {
    annotations.setTool("off");
    refreshAnnotateUI();
  }
}

// 動画の実アスペクトを得る。viewer の <video> が知っている videoWidth/Height
// を最優先 (WebRTC で解像度がネゴ済みのため最も正確)、無ければホストから来た
// メタ情報の originalWidth/Height にフォールバック。
function getAspectForIndex(index: 1 | 2 | 3): number | null {
  const video = els.videoGrid.querySelector<HTMLVideoElement>(
    `video[data-index="${index}"]`,
  );
  if (video && video.videoWidth > 0 && video.videoHeight > 0) {
    return video.videoWidth / video.videoHeight;
  }
  for (const meta of metadataByStreamId.values()) {
    if (
      meta.index === index &&
      meta.originalWidth > 0 &&
      meta.originalHeight > 0
    ) {
      return meta.originalWidth / meta.originalHeight;
    }
  }
  return null;
}

function getAllAspects(): number[] {
  const result: number[] = [];
  for (const idx of [1, 2, 3] as const) {
    const a = getAspectForIndex(idx);
    if (a !== null) result.push(a);
  }
  return result;
}

function updateLayoutAspect(): void {
  const layout = els.videoGrid.dataset.layout ?? "";

  els.videoGrid.style.removeProperty("aspect-ratio");
  els.videoGrid.style.removeProperty("grid-template-columns");
  els.videoGrid.style.removeProperty("grid-template-rows");

  // free / room モードはステージ全体を使う絶対配置キャンバスなので aspect 不要。
  if (layout === "free" || layout === "room") {
    sizeGridToViewport();
    if (layout === "free") {
      // video の固有比が変わった (ホスト側ウィンドウサイズ変更 / 初回 loadedmetadata) 際、
      // 各アイテムを面積保持で新アスペクトに合わせ直す。
      refitAllFreeItemsToAspect();
    }
    return;
  }

  // 大小構成 (main-right / main-bottom / pip) は main の実アスペクトで fit。
  // 1 全画面もメイン (唯一の動画) でフィット。
  // 横並び・縦並びは全体平均。
  const useMainOnly =
    layout === "main-right" ||
    layout === "main-bottom" ||
    layout === "pip" ||
    layout === ""; // 1-window

  let chosenAspect: number | null = null;
  if (useMainOnly) {
    chosenAspect = getAspectForIndex(1);
  }
  if (chosenAspect === null) {
    const aspects = getAllAspects();
    if (aspects.length === 0) {
      sizeGridToViewport();
      return;
    }
    chosenAspect = aspects.reduce((a, b) => a + b, 0) / aspects.length;
  }

  const layoutAspect = computeLayoutAspect(layout, chosenAspect);
  els.videoGrid.style.aspectRatio = String(layoutAspect);
  sizeGridToViewport();
}

function refreshLayout(): void {
  reorderVideosByMetaIndex();
  applyLayout();
}

// ===== レイアウトピッカー (アイコンドロップダウン) =====
// トグルボタンに現在のレイアウト図形のみ表示し、クリックで popup を開く。
// popup 内の各項目もアイコンのみで、accessibility 用に aria-label / title に
// レイアウト名 (i18n) を載せる。

function rebuildLayoutPicker(
  opts: ReturnType<typeof getLayoutOptions>,
  current: LayoutKey | null,
): void {
  // popup 中身を再構築
  els.layoutPickerPopup.innerHTML = "";
  for (const opt of opts) {
    const item = document.createElement("button");
    item.type = "button";
    item.className = "layout-picker-item";
    item.dataset.value = opt.value;
    item.setAttribute("role", "option");
    item.setAttribute("aria-label", opt.label);
    item.title = opt.label;
    if (opt.value === current) {
      item.dataset.selected = "true";
      item.setAttribute("aria-selected", "true");
    }
    item.innerHTML = getLayoutIconSvg(opt.value);
    els.layoutPickerPopup.appendChild(item);
  }
  // トグルボタンには現在のアイコンを反映
  if (current) {
    els.layoutPickerCurrent.innerHTML = getLayoutIconSvg(current);
    const label =
      opts.find((o) => o.value === current)?.label ?? t("toolbar.layoutLabel");
    els.layoutPickerToggle.setAttribute("aria-label", label);
    els.layoutPickerToggle.title = label;
  } else {
    els.layoutPickerCurrent.innerHTML = "";
  }
}

let layoutPopupOpen = false;
function openLayoutPopup(): void {
  if (layoutPopupOpen) return;
  layoutPopupOpen = true;
  els.layoutPickerPopup.hidden = false;
  els.layoutPickerToggle.setAttribute("aria-expanded", "true");
  els.layoutPicker.dataset.open = "true";
}
function closeLayoutPopup(): void {
  if (!layoutPopupOpen) return;
  layoutPopupOpen = false;
  els.layoutPickerPopup.hidden = true;
  els.layoutPickerToggle.setAttribute("aria-expanded", "false");
  delete els.layoutPicker.dataset.open;
}
function toggleLayoutPopup(): void {
  if (layoutPopupOpen) closeLayoutPopup();
  else openLayoutPopup();
}

let annotatePopupOpen = false;
function openAnnotatePopup(): void {
  if (annotatePopupOpen) return;
  annotatePopupOpen = true;
  els.annotatePopup.hidden = false;
  els.annotateToggle.setAttribute("aria-expanded", "true");
  els.annotatePicker.dataset.open = "true";
  // popup を開いた瞬間の Undo 可否を反映
  refreshAnnotateUI();
}
function closeAnnotatePopup(): void {
  if (!annotatePopupOpen) return;
  annotatePopupOpen = false;
  els.annotatePopup.hidden = true;
  els.annotateToggle.setAttribute("aria-expanded", "false");
  delete els.annotatePicker.dataset.open;
}
function toggleAnnotatePopup(): void {
  if (annotatePopupOpen) closeAnnotatePopup();
  else openAnnotatePopup();
}

// 現在の tool / color をボタン UI に反映 (data-selected と aria 状態、
// トグルボタン横の色ドット)。
function refreshAnnotateUI(): void {
  const tool = annotations.getTool();
  const color = annotations.getColor();
  const toolBtns = els.annotatePopup.querySelectorAll<HTMLButtonElement>(
    "[data-annot-tool]",
  );
  for (const btn of toolBtns) {
    const selected = btn.dataset.annotTool === tool;
    if (selected) btn.dataset.selected = "true";
    else delete btn.dataset.selected;
  }
  const colorBtns = els.annotatePopup.querySelectorAll<HTMLButtonElement>(
    "[data-annot-color]",
  );
  for (const btn of colorBtns) {
    const selected = btn.dataset.annotColor === color;
    if (selected) btn.dataset.selected = "true";
    else delete btn.dataset.selected;
  }
  const active = tool !== "off";
  if (active) {
    els.annotatePicker.dataset.active = "true";
    els.annotateCurrentDot.style.setProperty(
      "--annot-current",
      colorHexFor(color),
    );
  } else {
    delete els.annotatePicker.dataset.active;
    els.annotateCurrentDot.style.removeProperty("--annot-current");
  }
  els.annotateUndo.disabled = !annotations.canUndo();
}

function colorHexFor(c: AnnotColor): string {
  if (c === "red") return "#ef4444";
  if (c === "blue") return "#3b82f6";
  return "#facc15";
}

function selectLayoutValue(value: LayoutKey): void {
  const count = videoByStreamId.size;
  settings = persistPreferredLayout(count, value, settings);
  clearSwapSelection();
  // grid-template-* は CSS transition で補間できないので、View Transitions API
  // で要素ごとに位置/サイズ補間する。各 video には index ベースの
  // view-transition-name を付与済み。reduce-motion 時と未対応ブラウザは即時切替。
  const reduceMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  const startVT = (
    document as Document & {
      startViewTransition?: (cb: () => void) => unknown;
    }
  ).startViewTransition;
  if (reduceMotion || typeof startVT !== "function") {
    refreshLayout();
  } else {
    startVT.call(document, () => {
      refreshLayout();
    });
  }
  closeLayoutPopup();
}

// ===== 自由配置モード =====

// .video-grid 内の現在の自由配置アイテム index → .free-item ラッパ要素
const freeItemEls = new Map<1 | 2 | 3, HTMLDivElement>();

function wrapForFreeMode(): void {
  const indices: (1 | 2 | 3)[] = [];
  for (const video of videoByStreamId.values()) {
    const idx = Number(video.dataset.index);
    if (idx === 1 || idx === 2 || idx === 3) indices.push(idx);
  }
  indices.sort((a, b) => a - b);
  const count = indices.length;

  // 保存済みレイアウトを取得。無ければデフォルト生成。
  // 保存済みでも video の index 集合と一致しないものは無効として再生成。
  const saved = getFreeLayout(count, settings);
  let items: FreeLayoutItem[];
  if (saved && indices.every((i) => saved.some((s) => s.index === i))) {
    items = saved.filter((s) => indices.includes(s.index));
  } else {
    items = makeDefaultFreeLayout(indices, getAspectMap(), getStageAspect());
    settings = persistFreeLayout(count, items, settings);
  }

  // 既存の wrap を破棄
  unwrapFreeMode();

  // 各 video を .free-item でラップ
  for (const item of items) {
    const video = findVideoByIndex(item.index);
    if (!video) continue;
    const wrapper = createFreeItem(item, video);
    freeItemEls.set(item.index, wrapper);
    els.videoGrid.appendChild(wrapper);
  }
}

function unwrapFreeMode(): void {
  // .free-item から video を取り出して grid 直下に戻し、wrapper を削除
  for (const wrapper of freeItemEls.values()) {
    const video = wrapper.querySelector("video");
    if (video) {
      els.videoGrid.appendChild(video); // 直下に戻す (CSS リセット)
    }
    wrapper.remove();
  }
  freeItemEls.clear();
}

function findVideoByIndex(index: 1 | 2 | 3): HTMLVideoElement | null {
  for (const v of videoByStreamId.values()) {
    if (Number(v.dataset.index) === index) return v;
  }
  return null;
}

function getAspectMap(): Map<1 | 2 | 3, number> {
  const m = new Map<1 | 2 | 3, number>();
  for (const idx of [1, 2, 3] as const) {
    const a = getAspectForIndex(idx);
    if (a !== null) m.set(idx, a);
  }
  return m;
}

function getStageAspect(): number {
  const r = els.videoGrid.getBoundingClientRect();
  return r.height > 0 ? r.width / r.height : 16 / 9;
}

function createFreeItem(
  item: FreeLayoutItem,
  video: HTMLVideoElement,
): HTMLDivElement {
  const wrapper = document.createElement("div");
  wrapper.className = "free-item";
  wrapper.dataset.index = String(item.index);
  applyItemStyle(wrapper, item);

  // video を wrapper の中に移動
  wrapper.appendChild(video);

  // 4 隅のリサイズハンドル
  for (const corner of ["nw", "ne", "sw", "se"] as const) {
    const handle = document.createElement("div");
    handle.className = `free-resize free-resize-${corner}`;
    wrapper.appendChild(handle);
    attachResizeHandlers(wrapper, handle, item, corner);
  }

  attachDragHandlers(wrapper, item);

  return wrapper;
}

function applyItemStyle(el: HTMLDivElement, item: FreeLayoutItem): void {
  el.style.left = `${item.x}%`;
  el.style.top = `${item.y}%`;
  el.style.width = `${item.width}%`;
  el.style.height = `${item.height}%`;
  el.style.zIndex = String(item.z);
}

const MIN_WIDTH_PCT = 10;
const MIN_HEIGHT_PCT = 10;
// 自由配置のアスペクト未確定時のフォールバック (16:9)。
// videoWidth/Height が確定した時点で refit が呼ばれて正しい比に切り替わる。
const FALLBACK_ASPECT = 16 / 9;

function attachDragHandlers(
  wrapper: HTMLDivElement,
  item: FreeLayoutItem,
): void {
  wrapper.addEventListener("pointerdown", (e) => {
    if (e.button !== 0) return;
    // 必ず最前面に (リサイズハンドル クリック時も)
    raiseToTop(wrapper);
    // リサイズハンドル上は drag せず、リサイズハンドラに処理を譲る
    const target = e.target as HTMLElement;
    if (target.classList.contains("free-resize")) return;

    e.preventDefault();
    wrapper.setPointerCapture(e.pointerId);
    wrapper.classList.add("dragging");
    const startX = e.clientX;
    const startY = e.clientY;
    const startLeft = item.x;
    const startTop = item.y;
    const gridRect = els.videoGrid.getBoundingClientRect();

    const onMove = (ev: PointerEvent) => {
      const dxPct = ((ev.clientX - startX) / gridRect.width) * 100;
      const dyPct = ((ev.clientY - startY) / gridRect.height) * 100;
      item.x = clamp(startLeft + dxPct, 0, 100 - item.width);
      item.y = clamp(startTop + dyPct, 0, 100 - item.height);
      wrapper.style.left = `${item.x}%`;
      wrapper.style.top = `${item.y}%`;
    };
    const onUp = (ev: PointerEvent) => {
      wrapper.releasePointerCapture(ev.pointerId);
      wrapper.removeEventListener("pointermove", onMove);
      wrapper.removeEventListener("pointerup", onUp);
      wrapper.removeEventListener("pointercancel", onUp);
      wrapper.classList.remove("dragging");
      persistCurrentFreeLayout();
    };
    wrapper.addEventListener("pointermove", onMove);
    wrapper.addEventListener("pointerup", onUp);
    wrapper.addEventListener("pointercancel", onUp);
  });
}

type Corner = "nw" | "ne" | "sw" | "se";

// アスペクト比を video の固有比に固定したまま、対角線方向への投影で
// 4 隅ドラッグを処理する。固定する側 (反対側の角 = anchor) を px 座標で
// 求め、マウスの "成長" 方向への投影距離でラッパサイズを決める。
const CORNER_SIGN: Record<Corner, { x: 1 | -1; y: 1 | -1 }> = {
  se: { x: 1, y: 1 },
  ne: { x: 1, y: -1 },
  sw: { x: -1, y: 1 },
  nw: { x: -1, y: -1 },
};

function attachResizeHandlers(
  wrapper: HTMLDivElement,
  handle: HTMLElement,
  item: FreeLayoutItem,
  corner: Corner,
): void {
  handle.addEventListener("pointerdown", (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    handle.setPointerCapture(e.pointerId);
    wrapper.classList.add("resizing");

    const gridRect = els.videoGrid.getBoundingClientRect();
    const stageW = gridRect.width;
    const stageH = gridRect.height;
    if (stageW <= 0 || stageH <= 0) return;

    const aspect = getAspectForIndex(item.index) ?? FALLBACK_ASPECT;
    const s = CORNER_SIGN[corner];

    // 開始時のアイテム矩形 (px)
    const startLeftPx = (item.x / 100) * stageW;
    const startTopPx = (item.y / 100) * stageH;
    const startWpx = (item.width / 100) * stageW;
    const startHpx = (item.height / 100) * stageH;

    // anchor = 反対側の角 (操作中固定)
    const anchorXpx = s.x === 1 ? startLeftPx : startLeftPx + startWpx;
    const anchorYpx = s.y === 1 ? startTopPx : startTopPx + startHpx;

    // 最小サイズ (% から px へ換算、アスペクト固定なので両軸の最大側を採用)
    const minWpxBase = (MIN_WIDTH_PCT / 100) * stageW;
    const minHpxBase = (MIN_HEIGHT_PCT / 100) * stageH;
    const minWpx = Math.max(minWpxBase, minHpxBase * aspect);

    const onMove = (ev: PointerEvent) => {
      // anchor から見たマウス相対位置 (px)
      const dxRel = ev.clientX - gridRect.left - anchorXpx;
      const dyRel = ev.clientY - gridRect.top - anchorYpx;
      // dragged-corner 方向の "成長量" (正方向に伸ばすと正)
      const gx = dxRel * s.x;
      const gy = dyRel * s.y;
      // 対角線 (aspect, 1) 上への投影 (px)。負値はサイズ反転だが MIN でクランプ。
      const t = (gx * aspect + gy) / (aspect * aspect + 1);
      let newWpx = t * aspect;

      // 最大サイズ: anchor からステージ端までの距離 (アスペクト固定で両軸を満たす)
      const maxWpxFromX = s.x === 1 ? stageW - anchorXpx : anchorXpx;
      const maxHpxFromY = s.y === 1 ? stageH - anchorYpx : anchorYpx;
      const maxWpx = Math.min(maxWpxFromX, maxHpxFromY * aspect);

      newWpx = clamp(newWpx, minWpx, maxWpx);
      const newHpx = newWpx / aspect;

      // 新しい top-left
      const draggedXpx = anchorXpx + newWpx * s.x;
      const draggedYpx = anchorYpx + newHpx * s.y;
      const newLeftPx = Math.min(anchorXpx, draggedXpx);
      const newTopPx = Math.min(anchorYpx, draggedYpx);

      item.x = (newLeftPx / stageW) * 100;
      item.y = (newTopPx / stageH) * 100;
      item.width = (newWpx / stageW) * 100;
      item.height = (newHpx / stageH) * 100;
      applyItemStyle(wrapper, item);
    };
    const onUp = (ev: PointerEvent) => {
      handle.releasePointerCapture(ev.pointerId);
      handle.removeEventListener("pointermove", onMove);
      handle.removeEventListener("pointerup", onUp);
      handle.removeEventListener("pointercancel", onUp);
      wrapper.classList.remove("resizing");
      persistCurrentFreeLayout();
    };
    handle.addEventListener("pointermove", onMove);
    handle.addEventListener("pointerup", onUp);
    handle.addEventListener("pointercancel", onUp);
  });
}

// クリックされた item を最前面に持ってくる (z-index を全 item の最大 + 1)
function raiseToTop(wrapper: HTMLDivElement): void {
  let maxZ = 0;
  for (const w of freeItemEls.values()) {
    const z = Number(w.style.zIndex || "0");
    if (z > maxZ) maxZ = z;
  }
  const currentZ = Number(wrapper.style.zIndex || "0");
  if (currentZ < maxZ) {
    wrapper.style.zIndex = String(maxZ + 1);
    persistCurrentFreeLayout();
  }
}

// ===== 3D 部屋モード =====
// カメラ (中央) から見た複数の壁。マウスドラッグで yaw/pitch 回転。

let roomYaw = 0;          // 度。+ で右を向く
let roomPitch = 0;        // 度。+ で下を向く (CSS rotateX 正方向)
// 初期視点は壁距離 R の約 20% 手前進。1 video (R=600) で 120px、2-3 video (R=700) で 140px。
// R 確定前 (renderRoomMode 前) のモジュール初期化なので固定 px を採用 (典型 R=700 の 20%)。
let roomZoomZ = 140;
// dolly が頭打ち (壁直前) になった先で 2D scale を継ぎ足し、見かけ上の倍率を
// 更に伸ばす。perspective 上、純粋 dolly では factor が ~1.0 で頭打ちになるため。
let roomScale = 1;
// scale 中のカーソル中心ズーム用 translate。transform-origin: 0 0 と組み合わせ
// translate(tx, ty) scale(s) でカーソル直下の点を不動に保つ。
let roomScaleTx = 0;
let roomScaleTy = 0;
let roomCameraEl: HTMLDivElement | null = null;
const roomWallEls = new Map<number, HTMLDivElement>();
let roomLookHandlers: {
  down: (e: PointerEvent) => void;
  move: (e: PointerEvent) => void;
  up: (e: PointerEvent) => void;
  wheel: (e: WheelEvent) => void;
  dblclick: (e: MouseEvent) => void;
} | null = null;

const ROOM_PITCH_LIMIT = 60;   // 上下視点の上限 (度)
const ROOM_LOOK_SENSITIVITY = 0.35; // 度 / px
// dolly 範囲。引き上限 -300px (~1.5R)。前進上限は壁距離 R に対し
// 安全距離 50px を残した動的値 (= roomConfigR - 50)。
const ROOM_ZOOM_MIN = -300;
const ROOM_WALL_SAFETY = 50;
// wheel deltaY ピクセル当たりの dolly 量。1 ノッチ (~100px) で約 40px の前進。
const ROOM_WHEEL_FACTOR = 0.4;
// 2D scale 段の上限。dolly 頭打ち後、ここまで追加で拡大可能。
const ROOM_SCALE_MAX = 3.0;
// dolly 1px ≒ scale 1/ROOM_DOLLY_PER_SCALE。dolly フェーズと体感速度を揃える。
const ROOM_DOLLY_PER_SCALE = 200;

// 現在の壁距離 R (renderRoomMode で更新)。dolly 上限のクランプに用いる。
let roomConfigR = 700;

// 初期 / リセット時の dolly 量を R に対する割合で指定。
const ROOM_INITIAL_DOLLY_RATIO = 0.2;

// 各壁は半径 R の円周上に置き、隣接する壁のエッジが完全に一致するよう
// 角度を動的計算する (= 壁が連続した曲面のように繋がり、没入感を出す)。
// 制約: tan(2·atan(W/(2R))) < 2R/W → u = W/(2R) < 1/√3 ≈ 0.577。
// 安全のため wallW を wallH * 2 で上限クランプ。
type RoomConfig = {
  distance: number;
  wallHeight: number;
};

function pickRoomConfig(count: number): RoomConfig {
  // 1 ウィンドウは少し近めで大きく
  if (count === 1) return { distance: 600, wallHeight: 460 };
  return { distance: 700, wallHeight: 350 };
}

// 各壁の幅 W_i と距離 R から、隣接壁のエッジが空間内で完全に一致する
// 円周配置の角度 (度) を返す。中央を 0° として左右対称に並べる。
// 戻り値の i 番目 = i 番目の壁の中心の CSS rotateY 角度 (+ で左、- で右)。
function computeTilingAngles(widths: number[], distance: number): number[] {
  if (widths.length === 0) return [];
  const half = widths.map(
    (w) => (Math.atan(w / (2 * distance)) * 180) / Math.PI,
  );
  const totalHalf = half.reduce((s, h) => s + h, 0);
  // 左端 (= 一番大きい正の角度) から右へ歩く
  let leftEdge = totalHalf;
  const centers: number[] = [];
  for (let i = 0; i < widths.length; i++) {
    const h = half[i] ?? 0;
    centers.push(leftEdge - h);
    leftEdge -= 2 * h;
  }
  return centers;
}

function renderRoomMode(): void {
  // 既存の room シーンが残っていれば破棄
  unwrapRoomMode();

  // 壁の左→右配置は positionOrder の順に従う (ユーザーのスワップを反映)
  ensurePositionOrder();
  const indices = positionOrder.slice();
  const n = indices.length;
  if (n === 0) return;

  const config = pickRoomConfig(n);
  const { distance: R, wallHeight: wallH } = config;
  roomConfigR = R;

  // 先に各壁の幅を確定 (アスペクト比から)、その後に隣接エッジが揃う角度を計算
  type Spec = {
    index: 1 | 2 | 3;
    video: HTMLVideoElement;
    wallW: number;
  };
  const specs: Spec[] = [];
  for (const idx of indices) {
    const video = findVideoByIndex(idx);
    if (!video) continue;
    const aspect = getAspectForIndex(idx) ?? 16 / 9;
    // 横長動画 (21:9 等) で内側エッジがカメラ背後まで延びる事故を防ぐ上限
    const wallW = Math.min(wallH * aspect, wallH * 2);
    specs.push({ index: idx, video, wallW });
  }

  const angles = computeTilingAngles(
    specs.map((s) => s.wallW),
    R,
  );

  const camera = document.createElement("div");
  camera.className = "room-camera";
  roomCameraEl = camera;

  const ceiling = document.createElement("div");
  ceiling.className = "room-plane room-ceiling";
  camera.appendChild(ceiling);

  const floor = document.createElement("div");
  floor.className = "room-plane room-floor";
  camera.appendChild(floor);

  for (let i = 0; i < specs.length; i++) {
    const spec = specs[i];
    if (!spec) continue;
    const angle = angles[i] ?? 0;

    const wall = document.createElement("div");
    wall.className = "room-wall";
    wall.dataset.index = String(spec.index);
    wall.style.width = `${spec.wallW}px`;
    wall.style.height = `${wallH}px`;
    wall.style.marginLeft = `${-spec.wallW / 2}px`;
    wall.style.marginTop = `${-wallH / 2}px`;
    wall.style.transform = `rotateY(${angle}deg) translateZ(${-R}px)`;

    wall.appendChild(spec.video);
    camera.appendChild(wall);
    roomWallEls.set(spec.index, wall);
  }

  els.videoGrid.appendChild(camera);
  applyRoomCameraTransform();
  applyRoomGridTransform();
  attachRoomLookHandlers();

  // 壁を作り直したので、選択中の表示状態を新しい wall に再適用
  if (selectedSwapIndex !== null) applySwapSelection();
}

function unwrapRoomMode(): void {
  detachRoomLookHandlers();
  if (roomCameraEl) {
    // video を壁の中から取り出して grid 直下に戻す
    for (const wall of roomWallEls.values()) {
      const video = wall.querySelector("video");
      if (video) els.videoGrid.appendChild(video);
    }
    roomCameraEl.remove();
    roomCameraEl = null;
  }
  roomWallEls.clear();
  // room モード用に grid へ当てていた scale transform を解除
  // (他レイアウトでは stage-zoom 等が transform を管理する)
  els.videoGrid.style.removeProperty("transform");
  els.videoGrid.style.removeProperty("transform-origin");
  // yaw/pitch/dolly/scale は維持 (モード再選択時に前回視点復元)
}

function applyRoomCameraTransform(): void {
  if (!roomCameraEl) return;
  // 順序: yaw → pitch → camera-local translateZ (dolly)。
  // CSS は右の transform から先に適用されるので translateZ を右端に置くと
  // 回転後のカメラローカル Z 方向への dolly になり、視線方向にズームできる。
  roomCameraEl.style.transform = `rotateX(${roomPitch}deg) rotateY(${roomYaw}deg) translateZ(${roomZoomZ}px)`;
}

// dolly 頭打ち後に grid 自体へ 2D scale を当てる (perspective と独立に効く)。
// scale は中央起点 (3D の dolly 感覚と連続させる)、pan は translate で重ねる。
function applyRoomGridTransform(): void {
  if (
    roomScale > 1.0001 ||
    roomScaleTx !== 0 ||
    roomScaleTy !== 0
  ) {
    els.videoGrid.style.transformOrigin = "50% 50%";
    els.videoGrid.style.transform = `translate(${roomScaleTx}px, ${roomScaleTy}px) scale(${roomScale})`;
  } else {
    els.videoGrid.style.removeProperty("transform");
    els.videoGrid.style.removeProperty("transform-origin");
  }
}

// 画面座標 (x, y) から、いずれかの wall の getBoundingClientRect 内に
// 収まる wall を返す。3D 回転後の wall を Chrome のヒットテストが取りこぼした
// 場合のフォールバック。複数 wall が AABB 上で重なる場合は中心が最も近いものを優先。
function findWallAtScreenPoint(x: number, y: number): HTMLElement | null {
  let best: { wall: HTMLElement; dist: number } | null = null;
  for (const wall of roomWallEls.values()) {
    const rect = wall.getBoundingClientRect();
    if (
      rect.width <= 0 ||
      rect.height <= 0 ||
      x < rect.left ||
      x > rect.right ||
      y < rect.top ||
      y > rect.bottom
    ) {
      continue;
    }
    const cx = (rect.left + rect.right) / 2;
    const cy = (rect.top + rect.bottom) / 2;
    const dist = Math.hypot(x - cx, y - cy);
    if (best === null || dist < best.dist) {
      best = { wall, dist };
    }
  }
  return best?.wall ?? null;
}

// 3D で :hover が効かない端 wall のためのフォールバック hover。
// pointermove (= ドラッグ中でも fire) で wall を逆引きし .js-hover を付与。
function updateRoomHoverFromPoint(x: number, y: number): void {
  if (roomWallEls.size === 0) return;
  const w = findWallAtScreenPoint(x, y);
  for (const wall of roomWallEls.values()) {
    if (wall === w) wall.classList.add("js-hover");
    else wall.classList.remove("js-hover");
  }
}

function clearRoomHover(): void {
  for (const wall of roomWallEls.values()) {
    wall.classList.remove("js-hover");
  }
}

function attachRoomLookHandlers(): void {
  detachRoomLookHandlers();
  // 2 系統の drag: 壁を掴むと look-around、床/天井 (= 背景) を掴むと pan。
  // scale=1 のときは pan 不要なので背景 drag も look-around に流す。
  let active = false;       // look-around active
  let panActive = false;    // pan active
  let startX = 0;
  let startY = 0;
  let startYaw = 0;
  let startPitch = 0;
  let panStartX = 0;
  let panStartY = 0;
  let panStartTx = 0;
  let panStartTy = 0;
  // pointerdown の e.target を保存。pointerCapture を取った後の pointerup では
  // e.target が .video-grid に逸れるため、down 時の元の hit-test 結果を持つ。
  let downTarget: HTMLElement | null = null;

  const down = (e: PointerEvent) => {
    if (e.button !== 0) return;
    const target = e.target instanceof HTMLElement ? e.target : null;
    let onWall = !!target?.closest(".room-wall");
    // scale > 1 で grid に 2D transform を当てている場合、perspective と
    // 重なってブラウザの pointer hit-test が壁を取りこぼし e.target が
    // .video-grid に逸れることがある。視覚的に壁の上にカーソルがあるかを
    // 各壁の画面投影 AABB で確認して救済する。
    if (!onWall && roomWallEls.size > 0) {
      onWall = !!findWallAtScreenPoint(e.clientX, e.clientY);
    }
    // 壁の外 (床/天井/空間) で、かつ scale > 1 (pan 可能) なら pan モード
    if (!onWall && roomScale > 1.0001) {
      panActive = true;
      panStartX = e.clientX;
      panStartY = e.clientY;
      panStartTx = roomScaleTx;
      panStartTy = roomScaleTy;
      els.videoGrid.setPointerCapture(e.pointerId);
      els.videoGrid.classList.add("looking"); // grabbing カーソルを共用
      clearRoomHover();
      e.preventDefault();
      return;
    }
    // それ以外 (壁の上、または背景でも scale=1) は look-around
    active = true;
    startX = e.clientX;
    startY = e.clientY;
    startYaw = roomYaw;
    startPitch = roomPitch;
    roomLookMoved = false;
    downTarget = target;
    els.videoGrid.setPointerCapture(e.pointerId);
    els.videoGrid.classList.add("looking");
  };
  const move = (e: PointerEvent) => {
    if (panActive) {
      roomScaleTx = panStartTx + (e.clientX - panStartX);
      roomScaleTy = panStartTy + (e.clientY - panStartY);
      applyRoomGridTransform();
      return;
    }
    if (!active) {
      // ドラッグ外 (通常マウス移動) は JS hover を更新
      updateRoomHoverFromPoint(e.clientX, e.clientY);
      return;
    }
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    // しきい値超えの移動を「ドラッグ」と判定。後続の click でスワップ操作を抑制。
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
      roomLookMoved = true;
      // ドラッグ中は hover ハイライト消す (見やすさ)
      clearRoomHover();
    }
    // drag 右 → 視点右 (= 部屋を左へ回す = yaw 負)
    roomYaw = startYaw - dx * ROOM_LOOK_SENSITIVITY;
    // drag 下 → 視点下 (= 部屋を後傾 = pitch 正)
    roomPitch = clamp(
      startPitch + dy * ROOM_LOOK_SENSITIVITY,
      -ROOM_PITCH_LIMIT,
      ROOM_PITCH_LIMIT,
    );
    applyRoomCameraTransform();
  };
  const up = (e: PointerEvent) => {
    if (panActive) {
      panActive = false;
      try {
        els.videoGrid.releasePointerCapture(e.pointerId);
      } catch {
        // already released
      }
      els.videoGrid.classList.remove("looking");
      return;
    }
    if (!active) return;
    active = false;
    try {
      els.videoGrid.releasePointerCapture(e.pointerId);
    } catch {
      // already released
    }
    els.videoGrid.classList.remove("looking");

    // ドラッグ無し = タップ。click イベントは pointerCapture の影響で 3D の
    // wall まで届かないことがあるので、pointerdown 時に保存した downTarget を
    // 起点に壁を取り出す (それでも見つからなければ elementFromPoint をフォールバック)。
    const moved = roomLookMoved;
    const savedTarget = downTarget;
    downTarget = null;
    if (moved) return;

    let wall: HTMLElement | null = null;
    if (savedTarget) {
      wall = savedTarget.closest(".room-wall") as HTMLElement | null;
    }
    if (!wall) {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (el instanceof Element) {
        wall = el.closest(".room-wall") as HTMLElement | null;
      }
    }
    // 最後のフォールバック: preserve-3d 配下で Chrome のヒットテストが
    // 回転した wall を取りこぼした場合に備え、各 wall の画面投影 AABB と
    // クリック座標を照合して中心が最も近い wall を選ぶ。
    if (!wall) {
      wall = findWallAtScreenPoint(e.clientX, e.clientY);
    }

    if (wall) {
      const n = Number(wall.dataset.index);
      if (n === 1 || n === 2 || n === 3) handleSwapClick(n as 1 | 2 | 3);
    } else if (selectedSwapIndex !== null) {
      clearSwapSelection();
    }
  };

  // dolly zoom: ホイールでカメラ前進・後退。
  // ピンチ (ctrlKey 付き wheel) はブラウザ拡大に任せる。
  // 段階制ズーム: ホイール入力をまず dolly で消費し、頭打ち (壁直前) に
  // 達したぶんは 2D scale で消費する。逆向きはまず scale を 1.0 まで戻して
  // から dolly を戻す。これにより伸びが「途中で止まる」感覚を解消する。
  // 段階制ズーム: ホイール入力をまず dolly で消費し、頭打ち (壁直前) に
  // 達したぶんは 2D scale で消費する。逆向きはまず scale を 1.0 まで戻して
  // から dolly を戻す。scale は中央起点、pan は drag で別途行う。
  const wheel = (e: WheelEvent) => {
    if (e.ctrlKey) return;
    e.preventDefault();
    const dollyMax = Math.max(0, roomConfigR - ROOM_WALL_SAFETY);
    let delta = -e.deltaY * ROOM_WHEEL_FACTOR; // + で zoom in

    if (delta >= 0) {
      // zoom in: 残り dolly を使い切ってから scale へ流す
      const remaining = dollyMax - roomZoomZ;
      if (remaining > 0) {
        const consume = Math.min(delta, remaining);
        roomZoomZ += consume;
        delta -= consume;
      }
      if (delta > 0) {
        roomScale = Math.min(
          ROOM_SCALE_MAX,
          roomScale + delta / ROOM_DOLLY_PER_SCALE,
        );
      }
    } else {
      // zoom out: 先に scale を 1.0 まで戻し、残りは dolly を後ろへ
      let abs = -delta;
      if (roomScale > 1) {
        const scaleEnergy = (roomScale - 1) * ROOM_DOLLY_PER_SCALE;
        const consume = Math.min(abs, scaleEnergy);
        roomScale = Math.max(1, roomScale - consume / ROOM_DOLLY_PER_SCALE);
        abs -= consume;
      }
      if (abs > 0) {
        roomZoomZ = Math.max(ROOM_ZOOM_MIN, roomZoomZ - abs);
      }
    }

    // 等倍に戻ったら pan もリセット (画面いっぱいに戻すため translate 不要)
    if (roomScale <= 1.0001) {
      roomScale = 1;
      roomScaleTx = 0;
      roomScaleTy = 0;
    }

    applyRoomCameraTransform();
    applyRoomGridTransform();
  };
  // ダブルクリックで視点 (yaw/pitch/dolly/scale) をリセット。直前 2 クリックで
  // 起きた壁スワップ選択も同時に解除する。
  const dblclick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // 初期視点と同じ「少し近い」位置に戻す。R はその時点の構成で動的に決まる。
    roomZoomZ = roomConfigR * ROOM_INITIAL_DOLLY_RATIO;
    roomYaw = 0;
    roomPitch = 0;
    roomScale = 1;
    roomScaleTx = 0;
    roomScaleTy = 0;
    clearSwapSelection();
    applyRoomCameraTransform();
    applyRoomGridTransform();
  };

  els.videoGrid.addEventListener("pointerdown", down);
  els.videoGrid.addEventListener("pointermove", move);
  els.videoGrid.addEventListener("pointerup", up);
  els.videoGrid.addEventListener("pointercancel", up);
  els.videoGrid.addEventListener("wheel", wheel, { passive: false });
  els.videoGrid.addEventListener("dblclick", dblclick);

  roomLookHandlers = { down, move, up, wheel, dblclick };
}

function detachRoomLookHandlers(): void {
  if (!roomLookHandlers) return;
  els.videoGrid.removeEventListener("pointerdown", roomLookHandlers.down);
  els.videoGrid.removeEventListener("pointermove", roomLookHandlers.move);
  els.videoGrid.removeEventListener("pointerup", roomLookHandlers.up);
  els.videoGrid.removeEventListener("pointercancel", roomLookHandlers.up);
  els.videoGrid.removeEventListener("wheel", roomLookHandlers.wheel);
  els.videoGrid.removeEventListener("dblclick", roomLookHandlers.dblclick);
  roomLookHandlers = null;
  clearRoomHover();
}

// 全 free-item の現在アスペクトを再フィット。
// 各アイテムは pixel 面積 (W_px × H_px) と中心点を維持したまま、video の
// 現アスペクトに合致するよう width/height を再計算する。
// ステージに収まらない場合はアスペクト固定でステージ内に縮小、中心も
// ステージ内にクランプ。
function refitAllFreeItemsToAspect(): void {
  if (freeItemEls.size === 0) return;
  const gridRect = els.videoGrid.getBoundingClientRect();
  const stageW = gridRect.width;
  const stageH = gridRect.height;
  if (stageW <= 0 || stageH <= 0) return;

  let changed = false;
  for (const [idx, wrapper] of freeItemEls) {
    const aspect = getAspectForIndex(idx);
    if (aspect === null || !Number.isFinite(aspect) || aspect <= 0) continue;

    const oldLeftPx = (parsePct(wrapper.style.left) / 100) * stageW;
    const oldTopPx = (parsePct(wrapper.style.top) / 100) * stageH;
    const oldWpx = (parsePct(wrapper.style.width) / 100) * stageW;
    const oldHpx = (parsePct(wrapper.style.height) / 100) * stageH;
    if (oldWpx <= 0 || oldHpx <= 0) continue;

    const currentAspect = oldWpx / oldHpx;
    // ほぼ一致なら何もしない (浮動小数誤差吸収)
    if (Math.abs(currentAspect - aspect) < 0.005) continue;

    // 面積保持: newW × newH = oldArea, newW / newH = aspect
    const oldArea = oldWpx * oldHpx;
    let newHpx = Math.sqrt(oldArea / aspect);
    let newWpx = newHpx * aspect;

    // ステージに収まらない場合はアスペクト固定でスケールダウン
    if (newWpx > stageW) {
      newWpx = stageW;
      newHpx = newWpx / aspect;
    }
    if (newHpx > stageH) {
      newHpx = stageH;
      newWpx = newHpx * aspect;
    }

    // 中心保持
    const centerX = oldLeftPx + oldWpx / 2;
    const centerY = oldTopPx + oldHpx / 2;
    let newLeftPx = centerX - newWpx / 2;
    let newTopPx = centerY - newHpx / 2;
    newLeftPx = clamp(newLeftPx, 0, stageW - newWpx);
    newTopPx = clamp(newTopPx, 0, stageH - newHpx);

    const z = Number(wrapper.style.zIndex || "1");
    applyItemStyle(wrapper, {
      index: idx,
      x: (newLeftPx / stageW) * 100,
      y: (newTopPx / stageH) * 100,
      width: (newWpx / stageW) * 100,
      height: (newHpx / stageH) * 100,
      z,
    });
    changed = true;
  }
  if (changed) persistCurrentFreeLayout();
}

function persistCurrentFreeLayout(): void {
  const items: FreeLayoutItem[] = [];
  for (const [idx, wrapper] of freeItemEls) {
    items.push({
      index: idx,
      x: parsePct(wrapper.style.left),
      y: parsePct(wrapper.style.top),
      width: parsePct(wrapper.style.width),
      height: parsePct(wrapper.style.height),
      z: Number(wrapper.style.zIndex || "1"),
    });
  }
  const count = items.length;
  if (count === 0) return;
  settings = persistFreeLayout(count, items, settings);
}

async function toggleFullscreen(): Promise<void> {
  try {
    if (document.fullscreenElement) {
      await document.exitFullscreen();
    } else {
      await document.documentElement.requestFullscreen();
    }
  } catch (err) {
    console.warn("[WinCast viewer] fullscreen failed:", err);
  }
}

els.input.addEventListener("input", () => {
  const digits = normalizePincode(els.input.value).slice(0, PINCODE_LENGTH);
  const a = digits.slice(0, 3);
  const b = digits.slice(3, 6);
  const c = digits.slice(6, 9);
  const formatted = [a, b, c].filter((p) => p.length > 0).join("-");
  if (formatted !== els.input.value) {
    els.input.value = formatted;
  }
  const complete = digits.length === PINCODE_LENGTH;
  els.input.dataset.complete = complete ? "true" : "false";
  els.joinButton.dataset.ready = complete ? "true" : "false";
});

els.form.addEventListener("submit", (e) => {
  e.preventDefault();
  const pincode = normalizePincode(els.input.value);
  if (pincode.length !== PINCODE_LENGTH) {
    setFormStatusKey("join.pinLengthError", "error", { n: PINCODE_LENGTH });
    return;
  }
  void connect(pincode);
});

els.leaveButton.addEventListener("click", () => {
  // 明示切断: 自動再接続を無効化
  cancelReconnect();
  clearPincodeFromSession();
  teardown();
  setFormStatus("");
  showJoin();
  els.joinButton.disabled = false;
});

els.layoutPickerToggle.addEventListener("click", (e) => {
  e.stopPropagation();
  toggleLayoutPopup();
});
els.layoutPickerPopup.addEventListener("click", (e) => {
  const target = (e.target as HTMLElement | null)?.closest(
    ".layout-picker-item",
  ) as HTMLButtonElement | null;
  if (!target) return;
  const value = target.dataset.value as LayoutKey | undefined;
  if (!value) return;
  selectLayoutValue(value);
});
// popup の外側クリックで閉じる
document.addEventListener("click", (e) => {
  if (!layoutPopupOpen) return;
  const target = e.target as Node | null;
  if (target && els.layoutPicker.contains(target)) return;
  closeLayoutPopup();
});

// ===== annotate toolbar wiring =====
els.annotateToggle.addEventListener("click", (e) => {
  e.stopPropagation();
  toggleAnnotatePopup();
});
els.annotatePopup.addEventListener("click", (e) => {
  const target = e.target as HTMLElement | null;
  if (!target) return;
  const toolBtn = target.closest<HTMLButtonElement>("[data-annot-tool]");
  if (toolBtn) {
    const t = toolBtn.dataset.annotTool as AnnotTool | undefined;
    if (t) {
      // 既に選択中のツールをもう一度クリック → Off に戻す (トグル)
      annotations.setTool(annotations.getTool() === t ? "off" : t);
      refreshAnnotateUI();
    }
    return;
  }
  const colorBtn = target.closest<HTMLButtonElement>("[data-annot-color]");
  if (colorBtn) {
    const c = colorBtn.dataset.annotColor as AnnotColor | undefined;
    if (c) {
      annotations.setColor(c);
      // 色変更 = ペンを使い始める意図と解釈し、Off の場合は pen に切替
      if (annotations.getTool() === "off") annotations.setTool("pen");
      refreshAnnotateUI();
    }
    return;
  }
  if (target.closest("#annotate-undo")) {
    annotations.undo();
    refreshAnnotateUI();
    return;
  }
  if (target.closest("#annotate-clear")) {
    annotations.clearAll();
    refreshAnnotateUI();
  }
});
document.addEventListener("click", (e) => {
  if (!annotatePopupOpen) return;
  const target = e.target as Node | null;
  if (target && els.annotatePicker.contains(target)) return;
  closeAnnotatePopup();
});

els.fullscreenBtn.addEventListener("click", () => {
  void toggleFullscreen();
});

els.audioMuteBtn.addEventListener("click", () => {
  setAudioMuted(els.audioMuteBtn.dataset.muted !== "true");
});

document.addEventListener("keydown", (e) => {
  if (els.stageView.hidden) return;
  // Cmd+Z / Ctrl+Z で annotation を 1 個 undo (ツール選択中のみ)
  if ((e.metaKey || e.ctrlKey) && !e.shiftKey && (e.key === "z" || e.key === "Z")) {
    if (annotations.getTool() === "off") return;
    e.preventDefault();
    annotations.undo();
    refreshAnnotateUI();
    return;
  }
  if (e.key === "f" || e.key === "F") {
    e.preventDefault();
    void toggleFullscreen();
  } else if (e.key === "m" || e.key === "M") {
    if (els.audioMuteBtn.hidden) return;
    e.preventDefault();
    setAudioMuted(els.audioMuteBtn.dataset.muted !== "true");
  }
});

els.stageView.addEventListener("mousemove", scheduleOverlayHide);

// 動画クリックによる配置入れ替え。
// - 非対称系 (pip/main-right/main-bottom): 1 クリックで sub → main
// - 対称系 (horizontal/vertical/columns/rows): 2 段階クリックで 2 つを swap
// 3D 部屋 (room) は pointerCapture と click の相性が悪いため
// attachRoomLookHandlers の pointerup 側で処理する (ここでは扱わない)。
els.videoGrid.addEventListener("click", (e) => {
  if (els.videoGrid.dataset.layout === "room") return;
  const target = e.target as HTMLElement | null;
  if (!target) return;

  let idx: 1 | 2 | 3 | null = null;
  if (target instanceof HTMLVideoElement) {
    const n = Number(target.dataset.index);
    if (n === 1 || n === 2 || n === 3) idx = n as 1 | 2 | 3;
  }

  if (idx === null) {
    if (selectedSwapIndex !== null) clearSwapSelection();
    return;
  }

  if (isAsymmetricLayout()) {
    makeMain(idx);
  } else if (isSymmetricSwappableLayout()) {
    handleSwapClick(idx);
  }
});

// ウィンドウのリサイズ + ブラウザ拡大率の変化に追従:
// resize イベントは zoom 操作でも発火する。devicePixelRatio で baseline ビューポート
// (= 拡大率 1 換算の CSS px) を逆算するので、両方を毎回再計算するだけでよい。
window.addEventListener("resize", () => {
  sizeGridToViewport();
  applyZoomState();
});

// ===== 拡大時のドラッグパン =====
// .stage は overflow:auto で、grid が baseline サイズより縮んだビューポートを
// はみ出した状態で scrollLeft/Top を持つ。pointerdown でドラッグ開始し、移動量を
// scrollLeft/Top に反映してパンする。
// - 3D room: 既存の look (yaw/pitch) と競合するのでスキップ
// - free-item 内: 既存のアイテムドラッグと競合するのでスキップ
// - overlay 内: ボタンクリックを優先
(function attachStagePan(): void {
  let panning = false;
  let panPointerId = -1;
  let startClientX = 0;
  let startClientY = 0;
  let startScrollLeft = 0;
  let startScrollTop = 0;

  els.stageView.addEventListener("pointerdown", (e) => {
    if (e.button !== 0) return;
    const layout = els.videoGrid.dataset.layout ?? "";
    if (layout === "room") return;
    const target = e.target as HTMLElement;
    if (target.closest(".free-item")) return;
    if (target.closest(".stage-overlay")) return;
    // overflow が無いときはパン不要
    const overflowX =
      els.stageView.scrollWidth > els.stageView.clientWidth + 1;
    const overflowY =
      els.stageView.scrollHeight > els.stageView.clientHeight + 1;
    if (!overflowX && !overflowY) return;

    panning = true;
    panPointerId = e.pointerId;
    startClientX = e.clientX;
    startClientY = e.clientY;
    startScrollLeft = els.stageView.scrollLeft;
    startScrollTop = els.stageView.scrollTop;
    try {
      els.stageView.setPointerCapture(e.pointerId);
    } catch {
      // 一部 target ではキャプチャに失敗することがあるが致命的ではない
    }
    els.stageView.classList.add("panning");
    e.preventDefault();
  });

  els.stageView.addEventListener("pointermove", (e) => {
    if (!panning || e.pointerId !== panPointerId) return;
    els.stageView.scrollLeft = startScrollLeft - (e.clientX - startClientX);
    els.stageView.scrollTop = startScrollTop - (e.clientY - startClientY);
  });

  const stopPan = (e: PointerEvent) => {
    if (!panning || e.pointerId !== panPointerId) return;
    panning = false;
    try {
      els.stageView.releasePointerCapture(e.pointerId);
    } catch {
      // already released
    }
    els.stageView.classList.remove("panning");
  };
  els.stageView.addEventListener("pointerup", stopPan);
  els.stageView.addEventListener("pointercancel", stopPan);
})();

initStageZoom({
  stage: els.stageView,
  grid: els.videoGrid,
  isLayoutZoomable: () => {
    const layout = els.videoGrid.dataset.layout ?? "";
    // room は視点回転と衝突するので除外。free は onPointerDown 内で
    // .free-item を避けることで既存ドラッグと共存させる。
    if (layout === "" || layout === "room") return false;
    return true;
  },
});

annotations.init({ host: els.stageView });
annotations.onHistoryChange(() => {
  // 描画確定の度に Undo ボタンの disabled 状態を即時更新
  els.annotateUndo.disabled = !annotations.canUndo();
});

refreshAnnotateUI();

// ====== ロケール (EN / JA) の初期反映と切替 ======
// applyHtmlI18n() は data-i18n を attribute 単位で当てるが、document.title /
// meta description / og:* / twitter:* / og:locale / html lang は別途更新する必要がある。
// applyLocaleMeta() がそれらをまとめて差し替える。ロケール切替時にも再走する。
function applyLocaleMeta(): void {
  const loc = getLocale();
  document.title = t("meta.title");
  const desc = t("meta.description");
  document
    .querySelector<HTMLMetaElement>('meta[name="description"]')
    ?.setAttribute("content", desc);
  document
    .querySelector<HTMLMetaElement>('meta[property="og:title"]')
    ?.setAttribute("content", t("meta.title"));
  document
    .querySelector<HTMLMetaElement>('meta[property="og:description"]')
    ?.setAttribute("content", desc);
  document
    .querySelector<HTMLMetaElement>('meta[name="twitter:title"]')
    ?.setAttribute("content", t("meta.title"));
  document
    .querySelector<HTMLMetaElement>('meta[name="twitter:description"]')
    ?.setAttribute("content", desc);
  document
    .querySelector<HTMLMetaElement>('meta[property="og:locale"]')
    ?.setAttribute("content", loc === "ja" ? "ja_JP" : "en_US");
}

// ロケール切替時に refresh が必要な動的 UI:
// - data-i18n 属性 (applyHtmlI18n)
// - meta タグ / title (applyLocaleMeta)
// - 最終 setFormStatus*/setStageStatus* (記憶しておいた key を再 render)
// - ミュートボタンの aria-label / title (現在の muted 状態に応じて再評価)
// - レイアウトピッカー (オプション名を localized で再構築)
function rerenderDynamicI18n(): void {
  rerenderStatusI18n();
  if (!els.audioMuteBtn.hidden) {
    const muted = els.audioMuteBtn.dataset.muted === "true";
    const label = muted ? t("toolbar.muteOff") : t("toolbar.muteOn");
    els.audioMuteBtn.setAttribute("aria-label", label);
    els.audioMuteBtn.title = label;
  }
  // レイアウトピッカーは現アクティブ count から rebuild。stage 非表示中は何もしない。
  if (!els.layoutPicker.hidden) {
    refreshLayout();
  }
}

applyHtmlI18n();
applyLocaleMeta();

onLocaleChange((_loc: Locale) => {
  applyLocaleMeta();
  rerenderDynamicI18n();
});

els.langToggle.addEventListener("click", () => {
  setLocale(getLocale() === "en" ? "ja" : "en");
});

setFormStatus("");

// Join 画面の粒子背景アニメーション。stage-view 表示中は join-view が display:none
// となり、initJoinBg 内の rAF が getBoundingClientRect=0 で計算をスキップする。
{
  const joinBgCanvas = document.getElementById("join-bg");
  if (joinBgCanvas instanceof HTMLCanvasElement) {
    initJoinBg(joinBgCanvas);
  }
}

// About セクションの P2P ストリーミング解説アニメーション。
// 同じく join-view が hidden の間は rAF 内で計算スキップ。
{
  const heroCanvas = document.getElementById("hero-anim");
  const replayBtn = document.getElementById("replay-hero");
  if (heroCanvas instanceof HTMLCanvasElement) {
    initHeroAnim(
      heroCanvas,
      replayBtn instanceof HTMLButtonElement ? replayBtn : null
    );
  }
}

// セッション継続: sessionStorage に保存された pincode があれば自動再接続。
const restoredPincode = getPincodeFromSession();
if (restoredPincode) {
  const a = restoredPincode.slice(0, 3);
  const b = restoredPincode.slice(3, 6);
  const c = restoredPincode.slice(6, 9);
  els.input.value = [a, b, c].filter((p) => p.length > 0).join("-");

  // early-init.js が html に立てた hint を取り外す (以降は hidden 属性で制御)
  if (document.documentElement.dataset.autoReconnect === "1") {
    delete document.documentElement.dataset.autoReconnect;
  }
  // 常に stage を表示 (early-init.js が動いていない場合のフォールバックも兼ねる)
  els.joinView.hidden = true;
  els.stageView.hidden = false;
  setStageStatusKey("join.waitingHost");
  applyZoomState();

  // 直前 (リロード前) の同タブ WebSocket close を signaling 側で処理させ
  // "Viewer already attached" (409) の race を回避するための小さな遅延。
  // signaling 側でも viewer 上書き対応するため小さめで十分。
  window.setTimeout(() => {
    void connect(restoredPincode);
  }, VIEWER_RELOAD_RECONNECT_DELAY_MS);
}
